/**
 * SMS retention health — the read model behind the "SMS retention" operations panel.
 * Server: sms_retention_health() (migration 2026-10-02-041), admins/superadmins only.
 * Read-only: nothing here can turn retention on or off. The watchdog (migration 040) turns it off;
 * turning it back on is a deliberate SQL step after investigation.
 */
import { supabase } from "./supabase";

export interface SmsRetentionJobState {
  exists: boolean;
  active: boolean;
  schedule: string | null;
}

export interface SmsRetentionRun {
  id: number;
  started_at: string;
  finished_at: string | null;
  locks_held_ms: number | null;
  budget_exhausted: boolean | null;
  bodies_tombstoned: number | null;
  raw_scrubbed: number | null;
  attachments_cleared: number | null;
  snapshots_deleted: number | null;
  skipped: number | null;
}

export interface SmsRetentionFailedExecution {
  start_time: string;
  end_time: string | null;
  message: string | null;
}

export interface SmsRetentionTrip {
  id: number;
  tripped_at: string;
  reason: string;
  cron_was_active: boolean | null;
}

export interface SmsRetentionHealth {
  checked_at: string;
  tripwire_ms: number;
  retention: SmsRetentionJobState;
  watchdog: SmsRetentionJobState;
  runs: SmsRetentionRun[];
  failed_executions: SmsRetentionFailedExecution[];
  trips: SmsRetentionTrip[];
  held_past_24_months: number | null;
}

export async function getSmsRetentionHealth(): Promise<SmsRetentionHealth> {
  const { data, error } = await supabase.rpc("sms_retention_health");
  if (error) {
    if ((error.message ?? "").includes("NOT_AUTHORIZED")) {
      throw new Error(
        "Not authorized to read SMS retention health (requires an active admin or superadmin profile).",
      );
    }
    throw new Error(error.message);
  }
  return data as SmsRetentionHealth;
}
